/**
 * Catálogo de ejercicios: base y propios (Req. 9).
 *
 * - El catálogo base se agrega solo (asegurarCatalogoBase) y nunca se
 *   reescribe: lo que el usuario le cambió se respeta (Req. 9.7).
 * - No hay dos ejercicios activos con el mismo nombre, sin distinguir acentos
 *   ni mayúsculas (Req. 9.8).
 * - Un ejercicio con series registradas no se borra ni cambia de tipo: se
 *   archiva (Req. 9.6).
 */

import { db } from './schema';
import { enEscritura } from './tx';
import type { Exercise, UnidadCarga } from './types';
import { EJERCICIOS_BASE, type EjercicioBase } from '@/datos/ejercicios-base';
import { ahoraIso } from '@/domain/dates';
import { normalizar } from '@/domain/busqueda';
import { ErrorValidacion } from '@/domain/errors';

function desdeBase(b: EjercicioBase, ahora: string, archivado: boolean): Exercise {
  return {
    id: b.id,
    nombre: b.nombre,
    grupoMuscular: b.grupoMuscular,
    equipo: b.equipo,
    tipo: b.tipo,
    esBase: true,
    unidadCaptura: null,
    incrementoKg: null,
    notas: b.notas,
    archivado,
    creadoEn: ahora,
    actualizadoEn: ahora,
  };
}

/**
 * Agrega los ejercicios base que falten, sin tocar los que ya están. Si ya
 * hay un ejercicio propio activo con el mismo nombre, el base entra archivado
 * para no repetir el nombre. Idempotente. ESCRIBE: se llama al arrancar y
 * después de restaurar, nunca desde una consulta reactiva. Devuelve cuántos
 * agregó.
 */
export async function asegurarCatalogoBase(
  catalogo: readonly EjercicioBase[] = EJERCICIOS_BASE,
  ahora: string = ahoraIso(),
): Promise<number> {
  return enEscritura([db.exercises], async () => {
    const existentes = await db.exercises.toArray();
    const ids = new Set(existentes.map((x) => x.id));
    const activos = new Set(existentes.filter((x) => !x.archivado).map((x) => normalizar(x.nombre)));
    const nuevos: Exercise[] = [];
    for (const b of catalogo) {
      if (ids.has(b.id)) continue;
      const clave = normalizar(b.nombre);
      const repetido = activos.has(clave);
      if (!repetido) activos.add(clave);
      nuevos.push(desdeBase(b, ahora, repetido));
    }
    if (nuevos.length > 0) await db.exercises.bulkAdd(nuevos);
    return nuevos.length;
  });
}

/** LECTURA PURA: todos, por nombre. */
export async function leerEjercicios(): Promise<Exercise[]> {
  const todos = await db.exercises.toArray();
  return todos.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
}

export async function leerEjercicio(id: string): Promise<Exercise | undefined> {
  return db.exercises.get(id);
}

/** Cuántas series y rutinas usan el ejercicio. */
export async function usosDeEjercicio(id: string): Promise<{ series: number; rutinas: number }> {
  const [series, rutinas] = await Promise.all([
    db.workoutSets.where('exerciseId').equals(id).count(),
    db.routineExercises.where('exerciseId').equals(id).count(),
  ]);
  return { series, rutinas };
}

export function validarEjercicio(x: Exercise): void {
  const nombre = x.nombre.trim();
  if (nombre === '') throw new ErrorValidacion('Escribe el nombre del ejercicio.', 'nombre');
  if (nombre.length > 80) throw new ErrorValidacion('El nombre es demasiado largo.', 'nombre');
  if (x.notas !== null && x.notas.length > 300) throw new ErrorValidacion('Las notas son demasiado largas.', 'notas');
}

/**
 * Guarda un ejercicio propio nuevo o editado (put por id: tocar dos veces
 * "Guardar" no duplica). Del base solo cambian unidad y notas. ESCRIBE.
 */
export async function guardarEjercicio(x: Exercise): Promise<Exercise> {
  validarEjercicio(x);
  const limpio: Exercise = {
    ...x,
    nombre: x.nombre.trim().replace(/\s+/g, ' '),
    notas: x.notas === null || x.notas.trim() === '' ? null : x.notas.trim(),
    actualizadoEn: ahoraIso(),
  };
  await enEscritura([db.exercises, db.workoutSets], async () => {
    const anterior = await db.exercises.get(limpio.id);
    if (anterior?.esBase === true) {
      // El catálogo base conserva nombre, grupo, equipo y tipo
      await db.exercises.put({ ...anterior, unidadCaptura: limpio.unidadCaptura, notas: limpio.notas, actualizadoEn: limpio.actualizadoEn });
      return;
    }
    if (!limpio.archivado) {
      const clave = normalizar(limpio.nombre);
      const repetido = await db.exercises
        .filter((o) => o.id !== limpio.id && !o.archivado && normalizar(o.nombre) === clave)
        .first();
      if (repetido !== undefined) {
        throw new ErrorValidacion(`Ya existe "${repetido.nombre}". Usa ese o cambia el nombre.`, 'nombre');
      }
    }
    if (anterior !== undefined && anterior.tipo !== limpio.tipo) {
      const series = await db.workoutSets.where('exerciseId').equals(limpio.id).count();
      if (series > 0) {
        throw new ErrorValidacion('Este ejercicio ya tiene series registradas: su tipo no se puede cambiar.', 'tipo');
      }
    }
    await db.exercises.put({ ...limpio, esBase: false });
  });
  return limpio;
}

/** Recuerda la unidad de carga del ejercicio (Req. 9.5). ESCRIBE. */
export async function cambiarUnidadEjercicio(id: string, unidad: UnidadCarga): Promise<void> {
  await enEscritura([db.exercises], async () => {
    await db.exercises.update(id, { unidadCaptura: unidad, actualizadoEn: ahoraIso() });
  });
}

/** Archiva o saca del archivo. Al sacarlo revisa que no repita el nombre de otro activo. ESCRIBE. */
export async function archivarEjercicio(id: string, archivado: boolean): Promise<void> {
  await enEscritura([db.exercises], async () => {
    const x = await db.exercises.get(id);
    if (x === undefined) return;
    if (!archivado) {
      const clave = normalizar(x.nombre);
      const repetido = await db.exercises
        .filter((o) => o.id !== id && !o.archivado && normalizar(o.nombre) === clave)
        .first();
      if (repetido !== undefined) {
        throw new ErrorValidacion(`Ya hay un ejercicio activo llamado "${repetido.nombre}".`);
      }
    }
    await db.exercises.update(id, { archivado, actualizadoEn: ahoraIso() });
  });
}

/** Borra un ejercicio propio que nadie usa; si tiene series o está en una rutina, se archiva (Req. 9.6). ESCRIBE. */
export async function borrarEjercicio(id: string): Promise<'borrado' | 'archivado'> {
  return enEscritura([db.exercises, db.workoutSets, db.routineExercises], async () => {
    const x = await db.exercises.get(id);
    if (x === undefined) return 'borrado';
    const [series, rutinas] = await Promise.all([
      db.workoutSets.where('exerciseId').equals(id).count(),
      db.routineExercises.where('exerciseId').equals(id).count(),
    ]);
    if (x.esBase || series > 0 || rutinas > 0) {
      await db.exercises.update(id, { archivado: true, actualizadoEn: ahoraIso() });
      return 'archivado';
    }
    await db.exercises.delete(id);
    return 'borrado';
  });
}
